import axios from "axios";
import { Action, Dispatch } from "redux";
import { loadAndTrack } from "redux-request-loading";
import { ThunkDispatch } from "redux-thunk";

import * as actionTypes from "@teamprevent/common/questionnaires/questionnaireConfig/actionTypes";
import { QuestionnaireConfig } from "@teamprevent/common/questionnaires/questionnaireConfig/models";
import {
    questionnaireConfigLoaded,
    questionnaireConfigSaved,
    termsAndConditionsOfUseDefaultTextLoaded
} from "@teamprevent/common/questionnaires/questionnaireConfig/actionTypes";

import { ApplicationState } from "../applicationState";

import { AdditionalQuestionCreateModel } from "./model";
import { QuestionnaireConfigApi } from "./QuestionnaireConfigApi";

export const buildLoadQuestionnaireConfig = (questionnaireConfigApi: QuestionnaireConfigApi) =>
    ({ customerId, questionnaireType }: { customerId: string; questionnaireType: string }) => {
        return async (dispatch: Dispatch) => {
            const result = await loadAndTrack(dispatch, actionTypes.LOAD_QUESTIONNAIRE_CONFIG, questionnaireConfigApi.get(customerId, questionnaireType));
            dispatch(questionnaireConfigLoaded({ questionnaireType, ...result }));
        };
    };

export const loadQuestionnaireConfig = buildLoadQuestionnaireConfig(new QuestionnaireConfigApi(axios));

export const buildLoadTermsAndConditionsOfUseDefaultText = (questionnaireConfigApi: QuestionnaireConfigApi) =>
    () => {
        return async (dispatch: Dispatch) => {
            const result = await loadAndTrack(
                dispatch,
                actionTypes.LOAD_TERMS_AND_CONDITIONS_OF_USE_DEFAULT_TEXT,
                questionnaireConfigApi.getTermsAndConditionsOfUseDefaultText());
            dispatch(termsAndConditionsOfUseDefaultTextLoaded({ defaultText: result.questionText }));
        };
    };

export const loadTermsAndConditionsOfUseDefaultText = buildLoadTermsAndConditionsOfUseDefaultText(new QuestionnaireConfigApi(axios));

export const buildSaveQuestionnaireConfig = (questionnaireConfigApi: QuestionnaireConfigApi) =>
    (customerId: string, questionnaireType: string, questionnaireConfig: QuestionnaireConfig) => {
        return async (dispatch: Dispatch) => {
            await loadAndTrack(
                dispatch,
                actionTypes.SAVE_QUESTIONNAIRE_CONFIG,
                questionnaireConfigApi.save(customerId, questionnaireType, questionnaireConfig)
            );
            dispatch(questionnaireConfigSaved({ questionnaireType, ...questionnaireConfig }));
        };
    };

export const saveQuestionnaireConfig = buildSaveQuestionnaireConfig(new QuestionnaireConfigApi(axios));

export const buildQuestionnaireConfigAddAdditionalQuestion = (questionnaireConfigApi: QuestionnaireConfigApi) =>
    (questionModel: AdditionalQuestionCreateModel) => {
        return async (dispatch: ThunkDispatch<ApplicationState, undefined, Action>) => {
            const questionAdded = await loadAndTrack(
                dispatch,
                actionTypes.CREATE_ADDITIONAL_QUESTION,
                questionnaireConfigApi.createQuestion(questionModel.customerId, questionModel.questionnaireType, questionModel)
            );
            dispatch(questionnaireAdditionalQuestionAdded({
                questionnaireType: questionModel.questionnaireType,
                questionAdded
            }));
        };
    };

export const questionnaireConfigAddAdditionalQuestion = buildQuestionnaireConfigAddAdditionalQuestion(new QuestionnaireConfigApi(axios));

export const buildQuestionnaireConfigRemoveAdditionalQuestion = (questionnaireConfigApi: QuestionnaireConfigApi) =>
    (customerId: string, questionnaireType: string, questionId: string) => {
        return async (dispatch: ThunkDispatch<ApplicationState, undefined, Action>) => {
            await loadAndTrack(
                dispatch,
                actionTypes.REMOVE_ADDITIONAL_QUESTION,
                questionnaireConfigApi.removeQuestion(customerId, questionnaireType, questionId)
            );
            dispatch(questionnaireAdditionalQuestionRemoved({
                questionnaireType,
                questionId
            }));
        };
    };

export const questionnaireConfigRemoveAdditionalQuestion = buildQuestionnaireConfigRemoveAdditionalQuestion(new QuestionnaireConfigApi(axios));

const questionnaireAdditionalQuestionAdded = (payload: actionTypes.QuestionnaireConfigAdditionalQuestionAddedPayload) => ({
    type: actionTypes.QUESTIONNAIRE_CONFIG_ADDITIONAL_QUESTION_ADDED,
    payload
});

const questionnaireAdditionalQuestionRemoved = (payload: actionTypes.QuestionnaireConfigAdditionalQuestionRemovedPayload) => ({
    type: actionTypes.QUESTIONNAIRE_CONFIG_ADDITIONAL_QUESTION_REMOVED,
    payload
});
